import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const hrs = v => v == null ? '—' : v < 48 ? `${v} h` : `${(v/24).toFixed(1)} d`;
const Tile = ({ label, value, tone }) => (
  <div className="card" style={{ flex:1, minWidth:150, textAlign:'center' }}>
    <div style={{ fontSize:26, fontWeight:700, color: tone || 'var(--text)' }}>{value}</div>
    <div style={{ fontSize:12, color:'var(--text2)', marginTop:2 }}>{label}</div>
  </div>
);

export default function Analytics() {
  const { isAdmin, user } = useAuth();
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const [campus, setCampus] = useState('');
  const [campuses, setCampuses] = useState([]);
  const canAll = isAdmin || ['it_head','management'].includes(user?.role);
  const load = useCallback(() => {
    api.get('/analytics/overview', { params:{ days, ...(campus ? { campus_id:campus } : {}) } }).then(r => setD(r.data)).catch(()=>{});
  }, [days, campus]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canAll) api.get('/master/campuses').then(r => setCampuses(r.data.filter(c=>c.active))).catch(()=>{}); }, [canAll]);
  if (!d) return <div style={{ color:'var(--text2)' }}>Loading analytics…</div>;

  return (
    <div>
      <div className="flex-gap" style={{ justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:10, marginBottom:16 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>📈 Performance Analytics</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Live from task data — no manual reporting. Response SLA {d.sla_hours}h · overdue = past its priority target (High 1d / Normal 3d / Low 7d).</p>
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          {canAll && (
            <select value={campus} onChange={e => setCampus(e.target.value)} style={{ padding:'6px 8px' }}>
              <option value="">All campuses</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={{ padding:'6px 8px' }}>
            <option value={7}>Last 7 days</option><option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option><option value={365}>Last year</option>
          </select>
        </div>
      </div>

      <div className="flex-gap" style={{ gap:12, flexWrap:'wrap', marginBottom:14 }}>
        <Tile label="Open" value={d.open} />
        <Tile label="Overdue" value={d.overdue} tone={d.overdue>0?'var(--red)':'var(--green)'} />
        <Tile label="Escalated" value={d.escalated} tone={d.escalated>0?'var(--orange)':'var(--text)'} />
        <Tile label={`Completed (${d.days}d)`} value={d.completed_period} tone="var(--green)" />
        <Tile label="Reopened" value={d.reopened} tone={d.reopened>0?'var(--orange)':'var(--text)'} />
      </div>
      <div className="flex-gap" style={{ gap:12, flexWrap:'wrap', marginBottom:22 }}>
        <Tile label="Avg response" value={hrs(d.avg_response_h)} />
        <Tile label="Avg resolution" value={hrs(d.avg_resolution_h)} />
        <Tile label="Response SLA met" value={d.response_sla_pct==null?'—':`${d.response_sla_pct}%`}
          tone={d.response_sla_pct>=90?'var(--green)':d.response_sla_pct>=70?'var(--orange)':'var(--red)'} />
      </div>

      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>By engineer</div>
      <div className="card" style={{ padding:0, overflow:'hidden', marginBottom:22 }}>
        {!d.by_engineer.length ? <div className="empty-state"><p>No assigned tasks in range.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Engineer</th><th>Open</th><th>Overdue</th><th>Completed</th><th>Avg resolution</th></tr></thead>
            <tbody>{d.by_engineer.map((r,i) => (
              <tr key={i}>
                <td style={{ fontWeight:500 }}>{r.name}</td>
                <td>{r.open}</td>
                <td style={{ color:r.overdue>0?'var(--red)':'inherit' }}>{r.overdue}</td>
                <td style={{ color:'var(--green)' }}>{r.completed}</td>
                <td style={{ color:'var(--text2)' }}>{hrs(r.avg_resolution_h)}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>

      <div style={{ fontSize:13, fontWeight:600, marginBottom:8 }}>By campus</div>
      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {!d.by_campus.length ? <div className="empty-state"><p>No data in range.</p></div> : (
          <div className="table-wrap"><table>
            <thead><tr><th>Campus</th><th>Open</th><th>Overdue</th><th>Completed</th></tr></thead>
            <tbody>{d.by_campus.map((r,i) => (
              <tr key={i}>
                <td style={{ fontWeight:500 }}>{r.campus}</td>
                <td>{r.open}</td>
                <td style={{ color:r.overdue>0?'var(--red)':'inherit' }}>{r.overdue}</td>
                <td style={{ color:'var(--green)' }}>{r.completed}</td>
              </tr>
            ))}</tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
